// react
import {
	ChangeEvent,
	Dispatch,
	MouseEvent,
	SetStateAction,
	useContext,
	useEffect,
	useState,
} from 'react';

// types
import {
	ErrorCause,
	ExchangeRateByDate,
	Financier,
	Loan,
	LoanConversionPayload,
	LoanPayload,
} from '../../../@types';

// i18n
import { useTranslation } from 'react-i18next';

// router
import { useSearchParams } from 'react-router-dom';

// context
import { ContextBanner } from '../../../App.context';
import { ContextProject } from '../../pages/Project/Project.context';

// hooks
import { useExchangeRates } from '../../../hooks/useExchangeRates';
import { useFetch } from '../../../hooks/useFetch';

// mui
import { Alert, Grid, InputAdornment, SelectChangeEvent } from '@mui/material';

// utils
import { formatDate } from '../../../utils/dates';

// fields
import { AmountsConversions } from '../../fields/AmountsConversions/AmountsConversions';
import { ExchangeRates } from '../../fields/ExchangeRates/ExchangeRates';
import { Input } from '../../fields/Input/Input';
import { SelectCurrency } from '../../fields/SelectCurrency/SelectCurrency';

// ui
import { Loading } from '../../ui/Loading/Loading';

// icons
import {
	AccountBalanceOutlined as IconAccountBalance,
	PublicOutlined as IconPublic,
} from '@mui/icons-material';

// config
import { featureFlags } from '../../../config/featureFlags';

// props
export interface FormEditFinancingSourceProps {
	onClose?: () => void;
	onSuccess?: () => void;
	setIsLoading: Dispatch<SetStateAction<boolean>>;
}

export const FormEditFinancingSource = ({
	onClose,
	onSuccess,
	setIsLoading,
}: FormEditFinancingSourceProps) => {
	// hooks
	const { t } = useTranslation();
	const { getCurrenciesForExchangeRates } = useExchangeRates();

	// context
	const { setBanner } = useContext(ContextBanner);
	const { project } = useContext(ContextProject);

	// session
	const accessToken = sessionStorage.getItem('accessToken');
	const idToken = sessionStorage.getItem('idToken');

	// state
	const [error, setError] = useState<string | null>(null);
	const [exchangeRates, setExchangeRates] = useState<ExchangeRateByDate[]>([]);
	const [loan, setLoan] = useState<Loan | null>(null);
	const [searchParams] = useSearchParams();
	const [values, setValues] = useState({
		amount: 0,
		currency: '',
		dateApproved: '',
		dateClosing: '',
		financier: 'GOV' as Financier,
		name: '',
		num: '',
	});

	// params
	const paramLoan = searchParams.get('dialogLoan');

	// vars
	const idForm = `form-edit-loan`;

	// fetch loan
	const { isLoading: isLoadingLoan } = useFetch({
		isEnabled: Boolean(paramLoan),
		url: `${process.env.REACT_APP_API_URL}/loans/${paramLoan}?fields=id,name,financier,num,currency,amount,dateApproved,dateClosing,conversions`,
		options: {
			headers: {
				Authorization: `Bearer ${accessToken}`,
				User: String(idToken),
			},
		},
		onSuccess: (res) => {
			if (res.data && res.data.length) {
				setLoan(res.data[0]);
			}
		},
	});

	const handleChangeValue = (e: ChangeEvent | SelectChangeEvent<unknown>) => {
		const { name, value } = e.target as HTMLInputElement;
		setValues({
			...values,
			[name]: value,
		});
	};

	const handleChangeCurrency = (e: SelectChangeEvent<unknown>) => {
		const currency = String(e.target.value);

		setValues({
			...values,
			currency,
		});

		if (currency) {
			const currenciesRequired = getCurrenciesForExchangeRates({ currency });
			const exchangeRatesNew: ExchangeRateByDate[] = currenciesRequired.map((curr) => ({
				currency: curr,
				dateExchangeRate: '',
				exchangeRate: 0,
			}));

			setExchangeRates(exchangeRatesNew);
		} else {
			setExchangeRates([]);
		}
	};

	const handleChangeExchangeRates = (index: number, name: string, value: string | number) => {
		const exchangeRatesNew = [...exchangeRates];
		exchangeRatesNew[index] = {
			...exchangeRatesNew[index],
			[name]: value,
		};
		setExchangeRates(exchangeRatesNew);
	};

	const handleSubmit = async (e: MouseEvent<HTMLFormElement>) => {
		e.preventDefault();

		const form = document.querySelector(`#${idForm}`) as HTMLFormElement;
		const fd = new FormData(form);
		const entries = Object.fromEntries(fd.entries());

		try {
			// set loader
			setIsLoading(true);

			// clear error
			setError(null);

			if (!loan?.id) {
				throw new Error('Loan ID is required', {
					cause: {
						id: 'LOAN_ID_NON_EXISTENT',
					},
				});
			}

			const loanCode = `${loan.financier}${loan.num}`;

			// Define conversions using exchange rates
			const conversions: LoanConversionPayload[] = exchangeRates.map((exchangeRate) => {
				const conversionFieldName = `amount-conversion-${exchangeRate.currency}`;
				const convertedAmount = entries[conversionFieldName]
					? Number(entries[conversionFieldName])
					: 0;

				return {
					amount: convertedAmount,
					currency: exchangeRate.currency,
					dateExchangeRate: exchangeRate.dateExchangeRate,
					exchangeRate: exchangeRate.exchangeRate,
					tax: 0,
					type: 'AMOUNT',
				};
			});

			const body: Partial<LoanPayload> = {
				amount: entries.amount ? Number(entries.amount) : null,
				conversions,
				dateApproved: entries.dateApproved ? String(entries.dateApproved) : null,
				dateClosing: entries.dateClosing ? String(entries.dateClosing) : null,
				name: entries.name ? String(entries.name) : null,
			};

			const fetchEditFinancingSource = await fetch(
				`${process.env.REACT_APP_API_URL}/loans/${loan.id}`,
				{
					method: 'PATCH',
					headers: {
						Authorization: `Bearer ${accessToken}`,
						User: String(idToken),
					},
					body: JSON.stringify(body),
				}
			);
			const resEditFinancingSource = await fetchEditFinancingSource.json();

			if (resEditFinancingSource.error) {
				throw new Error(resEditFinancingSource.error.message, {
					cause: {
						id: resEditFinancingSource.error.id,
					},
				});
			}

			if (onSuccess) {
				onSuccess();
			}

			// set success banner
			setBanner({
				hasClose: true,
				message: t('alert.financingSourceUpdated', { id: loanCode }),
				severity: 'success',
			});

			// close dialog
			if (onClose) {
				onClose();
			}
		} catch (error) {
			const err = error as Error;
			const cause = err.cause as ErrorCause;

			let message = t('error.default');

			if (cause) {
				if (cause.id === 'LOAN_NON_EXISTENT') {
					message = t('error.loanNonExistent', { id: loan?.id });
				} else if (cause.id === 'LOAN_UNAUTHORIZED_PATCH') {
					message = t('error.unauthorized');
				}
			}

			// set error
			setError(message);
		} finally {
			// set loader
			setIsLoading(false);
		}
	};

	// set default values when loan changes
	useEffect(() => {
		if (loan && project) {
			// Format dates for HTML5 date inputs (yyyy-MM-dd)
			const dateApprovedFormatted = formatDate({ date: loan.dateApproved, format: 'YYYY-MM-DD' });
			const dateClosingFormatted = formatDate({ date: loan.dateClosing, format: 'YYYY-MM-DD' });

			setValues({
				amount: loan.amount || 0,
				currency: loan.currency || '',
				dateApproved: dateApprovedFormatted,
				dateClosing: dateClosingFormatted,
				financier: loan.financier,
				name: loan.name || '',
				num: loan.num || '',
			});

			// Build exchange rates for ALL project currencies (not just those with existing conversions)
			if (loan.currency) {
				const exchangeRatesNew: ExchangeRateByDate[] = [];

				// Create a map of existing conversions for quick lookup
				const existingConversions = new Map<
					string,
					{ dateExchangeRate: string; exchangeRate: number }
				>();
				if (loan.conversions && loan.conversions.length > 0) {
					loan.conversions.forEach((conversion) => {
						existingConversions.set(conversion.currency, {
							dateExchangeRate: formatDate({
								date: conversion.dateExchangeRate,
								format: 'YYYY-MM-DD',
							}),
							exchangeRate: conversion.exchangeRate || 0,
						});
					});
				}

				// Get currencies that need exchange rates using the hook
				const currenciesForExchangeRates = getCurrenciesForExchangeRates({
					currency: loan.currency,
				});

				currenciesForExchangeRates.forEach((currency) => {
					const existing = existingConversions.get(currency);
					exchangeRatesNew.push({
						currency,
						dateExchangeRate: existing?.dateExchangeRate || '',
						exchangeRate: existing?.exchangeRate || 0,
					});
				});

				setExchangeRates(exchangeRatesNew);
			}
		}
	}, [getCurrenciesForExchangeRates, loan, project]);

	// content
	const renderContent = () => {
		if (isLoadingLoan) {
			return (
				<Grid item xs={12}>
					<Grid alignItems="center" container justifyContent="center">
						<Grid item>
							<Loading />
						</Grid>
					</Grid>
				</Grid>
			);
		}

		if (!loan) {
			return (
				<Grid item xs={12}>
					<Alert severity="error">{t('error.loanNonExistent', { id: paramLoan })}</Alert>
				</Grid>
			);
		}

		return (
			<>
				<Grid item xs={12}>
					<Input
						disabled={true}
						id={`${idForm}-financier`}
						InputProps={{
							startAdornment: (
								<InputAdornment position="start">
									{values.financier === 'GOV' ? <IconAccountBalance /> : <IconPublic />}
								</InputAdornment>
							),
						}}
						label={t('financier')}
						name="financier"
						value={values.financier === 'GOV' ? t('government') : t('other')}
					/>
				</Grid>
				<Grid item xs={12}>
					<Input
						id={`${idForm}-name`}
						label={t('name')}
						name="name"
						onChange={handleChangeValue}
						required={true}
						value={values.name}
					/>
				</Grid>
				<Grid item xs={12} md={6}>
					<Input
						disabled={true}
						id={`${idForm}-num`}
						InputProps={{
							startAdornment: <InputAdornment position="start">{values.financier}</InputAdornment>,
						}}
						label={t('number')}
						name="num"
						value={values.num}
					/>
				</Grid>
				<Grid item xs={12} md={6}>
					<SelectCurrency
						disabled={true}
						id={`${idForm}-currency`}
						name="currency"
						onChange={handleChangeCurrency}
						required={true}
						value={values.currency}
					/>
				</Grid>
				{featureFlags['IFRS'] && exchangeRates.length > 0 && values.currency && (
					<Grid item xs={12}>
						<ExchangeRates
							currencyBase={values.currency}
							exchangeRates={exchangeRates}
							onChange={handleChangeExchangeRates}
						/>
					</Grid>
				)}
				<Grid item xs={12}>
					<AmountsConversions
						currency={values.currency}
						exchangeRates={exchangeRates}
						inputs={[
							{
								id: `${idForm}-amount`,
								name: 'amount',
								onChange: handleChangeValue,
								required: true,
								type: 'number',
								value: values.amount,
							},
						]}
						label={t('totalFinancedAmount')}
					/>
				</Grid>
				<Grid item xs={12} md={6}>
					<Input
						id={`${idForm}-date-approved`}
						label={t('approvedDate')}
						name="dateApproved"
						onChange={handleChangeValue}
						type="date"
						value={values.dateApproved}
					/>
				</Grid>
				<Grid item xs={12} md={6}>
					<Input
						id={`${idForm}-date-closing`}
						label={t('closingDate')}
						name="dateClosing"
						onChange={handleChangeValue}
						type="date"
						value={values.dateClosing}
					/>
				</Grid>
				{error && (
					<Grid item xs={12}>
						<Alert severity="error">{error}</Alert>
					</Grid>
				)}
			</>
		);
	};

	return (
		<form id={idForm} name="formEditFinancingSource" onSubmit={handleSubmit}>
			<Grid container spacing={2}>
				{renderContent()}
			</Grid>
		</form>
	);
};
